/**
 * Beat Handlers
 * Handles beat detection callbacks (kick, snare, hihat)
 */

import type { Mood, MusicAnalysis, EffectType } from './types.js';

/**
 * Kick beat handler
 */
export function onKickDetected(
  intensity: number,
  mood: Mood,
  musicAnalysis: MusicAnalysis,
  effectType: EffectType,
  isActive: boolean,
  triggerScreenFlash: (intensity: number, mood: Mood) => void,
  triggerParticleExplosion: (intensity: number, mood: Mood) => void,
  updateBackgroundMood: (mood: Mood, intensity: number) => void,
  triggerShockwave: (intensity: number) => void,
  applyCameraShake: (intensity: number, mood: Mood) => void,
  triggerExplosionEffect: (intensity: number, mood: Mood) => void,
  triggerColorStrobe: (intensity: number, mood: Mood) => void,
  triggerComplexityEffect: (musicAnalysis: MusicAnalysis, mood: Mood) => void
): void {
  if (!isActive) return;

  switch(effectType) {
    case 'background':
      updateBackgroundMood(mood, intensity);
      break;

    case 'visual':
      triggerScreenFlash(intensity, mood);
      triggerParticleExplosion(intensity, mood);
      triggerShockwave(intensity);
      applyCameraShake(intensity, mood);

      if (intensity > 0.8) {
        triggerExplosionEffect(intensity, mood);
        triggerColorStrobe(intensity, mood);
      }
      break;

    case 'ui':
      triggerScreenFlash(intensity, mood);
      break;

    case 'all':
    default:
      triggerScreenFlash(intensity, mood);
      triggerParticleExplosion(intensity, mood);
      updateBackgroundMood(mood, intensity);
      triggerShockwave(intensity);
      applyCameraShake(intensity, mood);

      if (intensity > 0.8) {
        triggerExplosionEffect(intensity, mood);
        triggerColorStrobe(intensity, mood);
      }
      break;
  }

  // Music complexity effects (common for all types)
  if (musicAnalysis.complexity > 0.7) {
    triggerComplexityEffect(musicAnalysis, mood);
  }
}

/**
 * Snare beat handler
 */
export function onSnareDetected(
  intensity: number,
  mood: Mood,
  _effectType: EffectType,
  isActive: boolean,
  triggerSharpFlash: (intensity: number, mood: Mood) => void,
  triggerElectricalEffect: (intensity: number) => void
): void {
  if (!isActive) return;

  triggerSharpFlash(intensity, mood);
  triggerElectricalEffect(intensity);
}

/**
 * Hihat beat handler
 */
export function onHihatDetected(
  intensity: number,
  _mood: Mood,
  _effectType: EffectType,
  isActive: boolean,
  triggerSubtleSparkle: (intensity: number) => void
): void {
  if (!isActive) return;

  triggerSubtleSparkle(intensity);
}

/**
 * Explosion effect (enhanced particles, no video)
 */
export function triggerExplosionEffect(
  intensity: number,
  mood: Mood,
  triggerParticleExplosion: (intensity: number, mood: Mood) => void
): void {
  triggerParticleExplosion(intensity * 1.5, mood);
}

/**
 * Electrical effect (enhanced flash, no video)
 */
export function triggerElectricalEffect(
  intensity: number,
  triggerScreenFlash: (intensity: number, mood: Mood) => void
): void {
  triggerScreenFlash(intensity * 1.2, 'energetic');
}

/**
 * Complexity effect
 */
export function triggerComplexityEffect(
  musicAnalysis: MusicAnalysis,
  mood: Mood,
  triggerParticleExplosion: (intensity: number, mood: Mood) => void
): void {
  const complexity = musicAnalysis.complexity;

  if (complexity > 0.8) {
    // Multiple particle explosions
    for (let i = 0; i < 3; i++) {
      setTimeout(() => {
        triggerParticleExplosion(complexity, mood);
      }, i * 150);
    }
  }
}
